import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Centrifuge, type Subscription } from 'centrifuge';
import { useQueryClient } from '@tanstack/react-query';
import { api, type Realm } from './api';

/**
 * The client half of the realtime tier.
 *
 * Centrifugo has been deployed since the beginning — in compose, in the chart, with a NATS broker
 * behind it and a `realtime.connect` permission gating it — and nothing in the client ever
 * connected. Three of the four data screens never refreshed at all, and the fourth polled every
 * five seconds.
 *
 * What arrives on a channel is a refetch hint, never state (INV-65): the server says "something
 * about this changed", the client invalidates and re-reads through the same REST path it already
 * trusts. That is why a dropped message costs nothing but a slower refresh, and why this file
 * never renders anything it receives.
 */

type Ctx = { client: Centrifuge | null };
const RealtimeContext = createContext<Ctx>({ client: null });

/** `{ token, expires_in, url }` from POST /realtime/connection-token. */
async function connectionToken(realm: Realm): Promise<{ token: string; url: string }> {
  return api<{ token: string; url: string }>('/realtime/connection-token', { method: 'POST', realm });
}

export function RealtimeProvider({
  realm,
  enabled,
  children
}: {
  realm: Realm;
  enabled: boolean;
  children: ReactNode;
}) {
  const [client, setClient] = useState<Centrifuge | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    let instance: Centrifuge | null = null;

    (async () => {
      let first: { token: string; url: string };
      try {
        first = await connectionToken(realm);
      } catch {
        // Realtime is advisory. If a token cannot be obtained the app runs exactly as it did
        // before this file existed — the screens still fetch, they just do not get pushed to.
        return;
      }
      if (disposed) return;

      instance = new Centrifuge(first.url, {
        token: first.token,
        // Called only when a token is actually needed, so this is a refresh rather than a poll.
        getToken: async () => (await connectionToken(realm)).token
      });
      instance.on('error', ctx => console.warn('realtime error', ctx));
      instance.connect();
      setClient(instance);
    })();

    return () => {
      disposed = true;
      instance?.disconnect();
      setClient(null);
    };
  }, [realm, enabled]);

  return <RealtimeContext.Provider value={useMemo(() => ({ client }), [client])}>{children}</RealtimeContext.Provider>;
}

/**
 * Subscribes to one channel and invalidates the given query keys whenever it publishes.
 *
 * `tokenPath` is the endpoint that mints a subscription token for the channel — a capability the
 * API issues only after checking ownership, which is why the client cannot simply name a channel
 * and listen to it.
 */
export function useRealtimeRefresh(
  channelTokenPath: string | null,
  invalidate: readonly unknown[][],
  options: { realm?: Realm; method?: string } = {}
) {
  const { client } = useContext(RealtimeContext);
  const queryClient = useQueryClient();
  // Held in a ref so a new array literal on each render does not resubscribe.
  const keysRef = useRef(invalidate);
  keysRef.current = invalidate;

  useEffect(() => {
    if (!client || !channelTokenPath) return;
    let sub: Subscription | null = null;
    let disposed = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const reread = () => {
      for (const key of keysRef.current) queryClient.invalidateQueries({ queryKey: key });
    };

    (async () => {
      let channel: string;
      let token: string;
      try {
        const issued = await api<{ channel: string; token: string }>(channelTokenPath, {
          method: options.method ?? 'POST',
          realm: options.realm
        });
        channel = issued.channel;
        token = issued.token;
      } catch {
        return; // advisory; the screen keeps working without it
      }
      if (disposed || !channel) return;

      // getSubscription first: Centrifugo allows one Subscription per channel per client, and two
      // screens can legitimately want the same one.
      sub =
        client.getSubscription(channel) ??
        client.newSubscription(channel, {
          token,
          getToken: async () =>
            (await api<{ token: string }>(channelTokenPath, { method: options.method ?? 'POST', realm: options.realm }))
              .token
        });
      sub.on('publication', ctx => {
        reread();
        /**
         * Once more, a moment later, when the hint says a run finished.
         *
         * `qa.run.completed` is consumed by two lanes under separate consumer groups: `persister`
         * writes the gate and the quality blocks, `notifier` publishes this hint. Nothing orders
         * them, so the push can arrive before the verdict has been committed -- the screen re-reads,
         * still sees `processing`, and caches the stage it was trying to leave.
         *
         * The list then sat on that until its 30s fallback poll, while a freshly navigated detail
         * page showed the real answer: the same data at two different moments, which reads as the
         * two screens disagreeing.
         *
         * A second read absorbs the race instead of out-running it. It costs one request per
         * finished run, where polling faster would cost one every few seconds for the whole run.
         * A terminal `status` is the only case that can race this way -- the intermediate stages
         * are written by the lane that publishes them.
         */
        const status = (ctx?.data as { status?: string | null } | undefined)?.status;
        if (status) timers.push(setTimeout(reread, 1500));
      });
      if (sub.state !== 'subscribed') sub.subscribe();
    })();

    return () => {
      disposed = true;
      // A pending re-read after unmount would invalidate queries no screen is watching, and on a
      // fast navigation would fire against a client that has already moved on.
      for (const timer of timers) clearTimeout(timer);
      sub?.removeAllListeners('publication');
    };
  }, [client, channelTokenPath, queryClient, options.realm, options.method]);
}
